import {Suspense} from "react";
import {ActivityIndicator, FlatList, Pressable, StyleSheet, Text, View} from "react-native";
import {type Href, router, useLocalSearchParams} from "expo-router";
import {MessageSquare, User} from "lucide-react-native";
import {useTranslation} from "react-i18next";
import {useCurrentTheme} from "@/theming/useCurrentTheme";
import {HeaderText} from "@/components/common/HeaderText";
import {useChatConversations, useChatUnread} from "@/data/chat-hooks";

export default function ChatListScreen() {
    const {orgId} = useLocalSearchParams<{orgId: string}>();
    const {theme} = useCurrentTheme();
    const {t} = useTranslation();

    return (
        <View style={{flex: 1, backgroundColor: theme.colors.background}}>
            <HeaderText title={t("chat.title")} onBackPress={() => router.back()} />
            <Suspense
                fallback={
                    <View style={styles.centered}>
                        <ActivityIndicator color={theme.colors.primary} />
                    </View>
                }
            >
                <ChatGroupList orgId={orgId} />
            </Suspense>
        </View>
    );
}

function ChatGroupList({orgId}: {orgId: string}) {
    const {theme} = useCurrentTheme();
    const {t} = useTranslation();
    const conversations = useChatConversations(orgId);
    const unread = useChatUnread(conversations.map((c) => c.channelId));

    if (conversations.length === 0) {
        return (
            <View style={styles.centered}>
                <MessageSquare color={theme.colors.textSecondary} size={32} />
                <Text style={{color: theme.colors.textSecondary, marginTop: 8}}>{t("chat.empty")}</Text>
            </View>
        );
    }

    return (
        <FlatList
            data={conversations}
            keyExtractor={(c) => c.channelId}
            contentContainerStyle={{padding: 12}}
            renderItem={({item}) => {
                const Icon = item.kind === "dm" ? User : MessageSquare;
                return (
                    <Pressable
                        onPress={() =>
                            // cast via unknown: Expo Router's typed-route union only includes this
                            // new route after `.expo/types` regenerates on the next `expo start`.
                            router.push({
                                pathname: "/organization/[orgId]/chat/[channelId]",
                                params: {orgId, channelId: item.channelId, name: item.name}
                            } as unknown as Href)
                        }
                        style={[styles.row, {backgroundColor: theme.colors.surface, borderColor: theme.colors.border}]}
                    >
                        <Icon color={theme.colors.primary} size={20} />
                        <Text
                            style={[styles.rowText, {color: theme.colors.text, fontWeight: unread[item.channelId] ? "700" : "600"}]}
                            numberOfLines={1}
                        >
                            {item.name}
                        </Text>
                        {unread[item.channelId] && (
                            <View
                                style={[styles.unreadDot, {backgroundColor: theme.colors.primary}]}
                                accessibilityLabel={t("chat.unread")}
                            />
                        )}
                    </Pressable>
                );
            }}
        />
    );
}

const styles = StyleSheet.create({
    centered: {flex: 1, alignItems: "center", justifyContent: "center"},
    row: {
        flexDirection: "row",
        alignItems: "center",
        gap: 12,
        padding: 14,
        borderRadius: 12,
        borderWidth: StyleSheet.hairlineWidth,
        marginBottom: 8
    },
    rowText: {fontSize: 16, fontWeight: "600", flexShrink: 1, flex: 1},
    unreadDot: {width: 10, height: 10, borderRadius: 5}
});
